import React, { useState, useEffect, useContext } from "react";
import {
  Button,
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Snackbar,
  Alert,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from "@mui/material";
import { CheckCircleOutline } from "@mui/icons-material";
import { getEntitiesByAppId, getDetectionBySensors, createTrigger, editTrigger, getTriggerById } from "../../api";
import { GlobalContext } from "../../GlobalProvider";
import { useNavigate, useParams } from "react-router-dom";
import DetectionConfigurator from "./DetectionConfigurator";
import DisplayIcon from "../Icon/DisplayIcon";

const CreateEditTrigger = () => {
  const { id } = useParams();
  const { fullApp } = useContext(GlobalContext);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [entityTypes, setEntityTypes] = useState([]);
  const [selectedEntityType, setSelectedEntityType] = useState(null);
  const [sensorDetections, setSensorDetections] = useState({});
  const [triggerName, setTriggerName] = useState("");
  const [triggerDescription, setTriggerDescription] = useState("");
  const [detectionConfigurations, setDetectionConfigurations] = useState([]);
  const [successToast, setSuccessToast] = useState(false);
  const [showConfigurator, setShowConfigurator] = useState(false);

  const [appliesToAllEntities, setAppliesToAllEntities] = useState(true);
  const [specificEntities, setSpecificEntities] = useState([]);
  const [allEntities, setAllEntities] = useState([]);
  const [showEntitySelector, setShowEntitySelector] = useState(false);

  const navigate = useNavigate();
  const steps = ["Select Entity Type", "Configure Sensors and Detections", "Select Entities", "Review and Submit"];

  useEffect(() => {
    if (fullApp) {
      setEntityTypes(fullApp.entities || []);
      loadEntities();
    }
  }, [fullApp]);

  

  const loadEntities = async () => {
    try {
      setLoading(true);
      const response = await getEntitiesByAppId(fullApp._id);
      setAllEntities(response.data.data);
    } catch (err) {
      setError("Failed to load entities.");
    } finally {
      setLoading(false);
    }
  };

    // Load existing trigger details if editing
    useEffect(() => {
      if (id) {
        setLoading(true);
        fetchTriggerDetails(id);
      }
    }, [id]);
  
    const fetchTriggerDetails = async (id) => {
      try {
        const response = await getTriggerById(id);
        const trigger = response.data.trigger;
  
        setTriggerName(trigger.name);
        setTriggerDescription(trigger.description);
        setSelectedEntityType(trigger.entityTypeId);
        setDetectionConfigurations(trigger.configurations);
        setAppliesToAllEntities(trigger.appliesToAllEntities);
        setSpecificEntities(trigger.specificEntities || []);
      } catch (err) {
        setError("Failed to fetch trigger details.");
      } finally {
        setLoading(false);
      }
    };

  const handleEntityTypeSelection = async (entityType) => {
    setSelectedEntityType(entityType);
    setLoading(true);
    try {
      const response = await getDetectionBySensors({ sensors: entityType.sensors.map((s) => s._id) });
      const groupedDetections = {};
      response.data.forEach((detection) => {
        detection.applicable_sensors.forEach((sensorId) => {
          if (!groupedDetections[sensorId]) groupedDetections[sensorId] = [];
          groupedDetections[sensorId].push(detection);
        });
      });
      setSensorDetections(groupedDetections);
    } catch (err) {
      setError("Failed to fetch detections for the selected entity type.");
    } finally {
      setLoading(false);
    }
  };
  const handleNext = () => setStep((prev) => Math.min(prev + 1, steps.length - 1));
 
  const handleBack = () => {
    if (step === 0) {
      navigate("/triggers"); // Navigate back to triggers list page when on step 0
    } else {
      setStep((prev) => Math.max(prev - 1, 0));
    }
  };

  const handleAddDetection = (newDetection) => {
    setDetectionConfigurations((prev) => [...prev, newDetection]);
    setShowConfigurator(false);
  };

  const handleSubmit = async () => {
    const payload = {
      appId: fullApp._id,
      name: triggerName,
      description: triggerDescription,
      entityTypeId: selectedEntityType._id,
      configurations: detectionConfigurations,
      appliesToAllEntities,
      specificEntities: appliesToAllEntities ? [] : specificEntities,
    };

    setLoading(true);
    try {
      if (id) await editTrigger(id, payload);
      else await createTrigger(payload);
      setSuccessToast(true);
      setTimeout(() => navigate("/triggers"), 1500);
    } catch (err) {
      setError("Failed to submit the trigger.");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveDetection = (index) => {
    setDetectionConfigurations((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Box display="flex" justifyContent="center" mt={4}>
      <Box width="1000px" p={2} boxShadow={3} borderRadius={4}>
        {error && (
          <Box mb={2} p={2} bgcolor="error.main" color="white" borderRadius={2}>
            <Typography variant="body1">{error}</Typography>
          </Box>
        )}

        <Stepper activeStep={step} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {step === 0 && (
          <Box mt={4}>
            <Typography variant="h6" sx={{ marginBottom: 2 }}>
              Monitor Details
            </Typography>
            <TextField
              fullWidth
              value={triggerName}
              onChange={(e) => setTriggerName(e.target.value)}
              placeholder="Enter monitor name"
              variant="outlined"
              sx={{ marginBottom: 3 }}
            />
             <TextField
              fullWidth
              value={triggerDescription}
              onChange={(e) => setTriggerDescription(e.target.value)}
              placeholder="Enter monitor description"
              variant="outlined"
              sx={{ marginBottom: 3 }}
            />
            <Typography variant="h6">Select an Entity Type</Typography>
            <Grid container spacing={2}>
              {entityTypes.map((entityType) => (
                <Grid item xs={4} key={entityType._id}>
                  <Card
                    onClick={() => handleEntityTypeSelection(entityType)}
                    style={{
                      border: selectedEntityType?._id === entityType._id ? "2px solid green" : "none",
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        {/* Icon on the left */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <DisplayIcon name={entityType.icon} />
                          <Box>
                            <Typography variant="subtitle1">{entityType.name}</Typography>
                            <Typography variant="body2" color="textSecondary">
                              {entityType.description}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Conditional success icon on the right */}
                        {selectedEntityType?._id === entityType._id && <CheckCircleOutline color="success" />}
                      </Box>
                    </CardContent>

                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

{step === 1 && (
  <Box mt={4}>
    <Typography variant="h6" sx={{ mb: 2 }}>
      Configure Detections
    </Typography>
    <Button 
      variant="contained" 
      onClick={() => setShowConfigurator(true)} 
      sx={{ mb: 2 }}
    >
      Add Detection
    </Button>

    {detectionConfigurations.length > 0 ? (
      <Box mt={2}>
        <Grid container spacing={2}>
          {detectionConfigurations.map((config, index) => (
            <Grid item xs={12} key={index}>
              <Card sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {config.logic} Condition
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Sensor:</strong> {config.sensorName} ({config.sensorId})
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Detection:</strong> {config.detectionName} ({config.detectionId})
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Applied On:</strong> {config.appliedOn}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Parameters:</strong> Min {config.params.min}, Max {config.params.max}
                  </Typography>
                </Box>
                <Button 
                  variant="outlined" 
                  color="error" 
                  onClick={() => handleRemoveDetection(index)}
                >
                  Remove
                </Button>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    ) : (
      <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
        No detections added yet.
      </Typography>
    )}
  </Box>
)}

        {step === 2 && (
          <Box mt={4}>
            <Typography variant="h6">Select Entities</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={appliesToAllEntities}
                  onChange={(e) => setAppliesToAllEntities(e.target.checked)}
                />
              }
              label="Apply to All Entities"
            />
            {!appliesToAllEntities && (
              <Button variant="contained" onClick={() => setShowEntitySelector(true)}>
                Select Entities
              </Button>
            )}
            <Dialog open={showEntitySelector} onClose={() => setShowEntitySelector(false)} fullWidth maxWidth="sm">
              <DialogTitle>Select Entities</DialogTitle>
              <DialogContent>
                <Grid container spacing={2}>
                  {allEntities.map((entity) => (
                    <Grid item xs={12} key={entity._id}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={specificEntities.includes(entity._id)}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              setSpecificEntities((prev) =>
                                isChecked ? [...prev, entity._id] : prev.filter((id) => id !== entity._id)
                              );
                            }}
                          />
                        }
                        label={entity.name}
                      />
                    </Grid>
                  ))}
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setShowEntitySelector(false)} color="secondary">
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}

        {step === 3 && (
          <Box mt={4}>
            <Typography variant="h6">Review and Submit</Typography>
            <Box mt={2}>
              <Typography>Trigger Name: {triggerName}</Typography>
              <Typography>Entity Type: {selectedEntityType?.name}</Typography>
              <Typography>Applies to All Entities: {appliesToAllEntities ? "Yes" : "No"}</Typography>
              {!appliesToAllEntities && (
                <Typography>
                  Specific Entities: {specificEntities.map((id) => allEntities.find((e) => e._id === id)?.name).join(", ")}
                </Typography>
              )}
              <Typography>Configurations:</Typography>
              <pre>{JSON.stringify(detectionConfigurations, null, 2)}</pre>
            </Box>
          </Box>
        )}

        <Box mt={4} display="flex" justifyContent="space-between">
          <Button onClick={handleBack} >
            Back
          </Button>
          {step < steps.length - 1 ? (
            <Button onClick={handleNext} variant="contained">
              Next
            </Button>
          ) : (
            <Button onClick={handleSubmit} variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={20} /> : "Submit"}
            </Button>
          )}
        </Box>
      </Box>

      {showConfigurator && (
        <DetectionConfigurator
          sensors={selectedEntityType?.sensors || []}
          sensorDetections={sensorDetections}
          onAdd={handleAddDetection}
          onClose={() => setShowConfigurator(false)}
        />
      )}

      <Snackbar
        open={successToast}
        autoHideDuration={3000}
        onClose={() => setSuccessToast(false)}
      >
        <Alert onClose={() => setSuccessToast(false)} severity="success" sx={{ width: "100%" }}>
          Trigger created successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateEditTrigger;
